import { useState } from "react";
import { MoreHorizontal, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

// Create (manual or from current filters) or rename a collection
export const CollectionDialog = ({ state, onClose, filters, sort, onSaved }) => {
  const editing = state?.collection;
  const [f, setF] = useState(null);
  const v = f || { name: editing?.name || "", description: editing?.description || "", kind: editing?.kind || state?.kind || "manual" };
  const save = async (e) => {
    e.preventDefault();
    const body = { ...v, filters: editing ? editing.filters : v.kind === "filter" ? filters : {}, sort: editing ? editing.sort : sort };
    try {
      const { data } = editing ? await api.put(`/writing/collections/${editing.id}`, body) : await api.post("/writing/collections", body);
      toast.success(editing ? "Collection updated" : `"${data.name}" created`); onSaved(data, !editing); setF(null); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) { setF(null); onClose(); } }}>
      <DialogContent className="bg-panel border-line" data-testid="collection-dialog">
        <DialogTitle className="font-display text-3xl text-amber-50">{editing ? "Edit collection" : "New collection"}</DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">Collections never delete works, and a work can sit in as many as you like.</DialogDescription>
        <form onSubmit={save} className="space-y-4">
          <Input required maxLength={80} value={v.name} onChange={(e) => setF({ ...v, name: e.target.value })} placeholder="e.g. Gift fics" data-testid="collection-name-input" />
          <Textarea rows={2} maxLength={500} value={v.description} onChange={(e) => setF({ ...v, description: e.target.value })} placeholder="Optional description" data-testid="collection-description-input" />
          {!editing && (
            <div className="grid grid-cols-2 gap-2">
              {[["manual", "Hand-picked", "You add works yourself"], ["filter", "From current filters", "Fills itself automatically"]].map(([k, l, d]) => (
                <button key={k} type="button" onClick={() => setF({ ...v, kind: k })} aria-pressed={v.kind === k} data-testid={`collection-kind-${k}`}
                  className={`rounded-lg border p-3 text-left transition-colors ${v.kind === k ? "border-amber-500/60 bg-amber-500/10" : "border-line hover:border-slate-500"}`}>
                  <span className="block text-sm text-slate-100">{l}</span><span className="block text-[11px] text-slate-500">{d}</span>
                </button>
              ))}
            </div>
          )}
          <button className="w-full h-10 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold text-sm" data-testid="collection-save-btn">{editing ? "Save" : "Create collection"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Pill = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`shrink-0 inline-flex items-center gap-2 rounded-full border px-4 h-9 text-sm transition-colors ${on ? "border-amber-500 bg-amber-500/15 text-amber-100" : "border-line text-slate-400 hover:text-slate-200"}`}>{children}</button>
);

export const CollectionsBar = ({ collections, counts, total, current, setCurrent, onNew, onEdit, onEditFilters, onDeleted }) => {
  const [del, setDel] = useState(null);
  const remove = async () => {
    try { await api.delete(`/writing/collections/${del.id}`); toast.success(`"${del.name}" deleted. Your works are untouched.`); onDeleted(del.id); }
    catch (e) { toast.error(formatApiError(e)); } finally { setDel(null); }
  };
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1" data-testid="collections-bar">
      <Pill on={!current} onClick={() => setCurrent(null)} testId="collection-pill-all">All works <span className="font-code text-[10px] text-slate-500">{total}</span></Pill>
      {collections.map((c) => (
        <div key={c.id} className="shrink-0 flex items-center">
          <Pill on={current === c.id} onClick={() => setCurrent(c.id)} testId={`collection-pill-${c.id}`}>
            {c.name}<span className="font-code text-[10px] text-slate-500" data-testid={`collection-count-${c.id}`}>{counts[c.id] ?? 0}</span>
          </Pill>
          {current === c.id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button className="p-1.5 text-slate-500 hover:text-amber-300" data-testid={`collection-menu-${c.id}`}><MoreHorizontal className="w-4 h-4" /></button></DropdownMenuTrigger>
              <DropdownMenuContent className="bg-panel border-line">
                <DropdownMenuItem onSelect={() => onEdit(c)} data-testid="collection-edit">Rename or describe</DropdownMenuItem>
                {c.kind === "filter" && <DropdownMenuItem onSelect={() => onEditFilters(c)} data-testid="collection-edit-filters">Edit filters</DropdownMenuItem>}
                <DropdownMenuItem onSelect={() => setDel(c)} className="text-rose-300" data-testid="collection-delete">Delete collection</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ))}
      <button onClick={onNew} className="shrink-0 inline-flex items-center gap-1 rounded-full border border-dashed border-line2 px-4 h-9 text-sm text-slate-400 hover:text-amber-300 hover:border-amber-500/40" data-testid="collection-new-btn"><Plus className="w-3.5 h-3.5" /> New collection</button>
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-panel border-line" data-testid="collection-delete-dialog">
          <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{del?.name}"?</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">Only the collection goes. Every work in it stays where it is.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="collection-delete-confirm">Delete collection</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
